// Экран поиска (SPEC §4.1 маршрут /search, FR-SEARCH). Глобальный/по-чату поиск через transport.rest
// (search — не-стор-данные, §4.0); результаты → переход в чат с доскроллом к сообщению (канон L).
// Поиск включает расшифровки голосовых (бэкенд search §инв.3) — здесь лишь поверхность.

import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { ChatId, SearchResult } from '@tele-chat/shared-contracts'
import { IconButton, Spinner } from '@tele-chat/design-system'
import { useRest } from '../transport/TransportContext.js'
import { EmptyState } from '../components/EmptyState.js'
import { buildMessageDeepLink } from '../routing/deep-link.js'
import { strings } from '../i18n/strings.js'

export function SearchScreen(): JSX.Element {
  const rest = useRest()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const chatId = (params.get('chatId') ?? undefined) as ChatId | undefined

  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null)

  const run = useCallback(
    async (q: string): Promise<void> => {
      if (q.trim().length === 0) {
        setResults([])
        setSearched(false)
        return
      }
      setLoading(true)
      try {
        const r = await rest.search({
          scope: chatId !== undefined ? 'chat' : 'global',
          q: q.trim(),
          chatId,
        })
        setResults(r)
      } catch {
        setResults([])
      } finally {
        setLoading(false)
        setSearched(true)
      }
    },
    [rest, chatId],
  )

  useEffect(() => {
    if (debounce.current !== null) clearTimeout(debounce.current)
    debounce.current = setTimeout(() => void run(query), 300)
    return () => {
      if (debounce.current !== null) clearTimeout(debounce.current)
    }
  }, [query, run])

  const header: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    padding: 'var(--space-2) var(--space-3)',
    borderBottom: '1px solid var(--color-divider)',
    background: 'var(--color-surface)',
  }
  const input: CSSProperties = {
    flex: 1,
    border: 'none',
    outline: 'none',
    background: 'var(--color-bg-secondary)',
    borderRadius: 'var(--radius-pill)',
    padding: '8px var(--space-3)',
    color: 'var(--color-text)',
    fontSize: 'var(--font-md)',
  }

  return (
    <div data-screen="search" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={header}>
        <IconButton icon="back" label={strings.nav.back} onClick={() => navigate(-1)} />
        <input
          data-search-input=""
          autoFocus
          value={query}
          placeholder={strings.search.placeholder}
          onChange={(e) => setQuery(e.target.value)}
          style={input}
        />
      </div>

      <div data-search-scope="" style={{ padding: '4px var(--space-3)', fontSize: 'var(--font-xs)', color: 'var(--color-text-muted)' }}>
        {chatId !== undefined ? strings.search.scopeChat : strings.search.scopeGlobal}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-4)' }}>
          <Spinner />
        </div>
      ) : results.length > 0 ? (
        <div data-search-results="" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {results.map((r) => (
            <button
              key={r.messageId}
              type="button"
              data-search-result={r.messageId}
              onClick={() => navigate(buildMessageDeepLink(r.chatId, r.messageId))}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                border: 'none',
                background: 'transparent',
                borderBottom: '1px solid var(--color-divider)',
                padding: 'var(--space-3)',
                color: 'var(--color-text)',
                cursor: 'pointer',
              }}
            >
              <span data-snippet="" style={{ display: 'block', fontSize: 'var(--font-md)' }}>
                {r.snippet}
              </span>
            </button>
          ))}
        </div>
      ) : searched ? (
        <EmptyState title={strings.empty.searchNothing} />
      ) : null}
    </div>
  )
}
